import { LitElement, html, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import 'Components/atoms/vm-icon-next/vm-icon-next';
import Styles from './vm-video-comment-attachment-link.scss';

/**
 * Inline link to a legacy non-image attachment posted in a comment. Renders
 * as a 63×63 chip matching the image-thumbnail atom's footprint, with a
 * `file-06` icon centered on a light-gray surface. The chip wraps an
 * `<a target="_blank" rel="noopener">` so the browser handles open vs.
 * download per the attachment's content-type — no `download` attribute,
 * no preview iframe.
 *
 * Used in posted comments when an attachment's MIME isn't in the image
 * whitelist (replaces the old PDF-in-Google-Viewer iframe path).
 *
 * @attr href     - URL of the attachment. Required — when empty, the
 *                   element renders nothing (an icon-only chip with no
 *                   destination would be misleading UI).
 * @attr filename - Optional filename used for the accessible name. When
 *                   omitted, the last path segment of `href` is decoded and
 *                   used. Falls back to "Open attachment" if neither yields
 *                   a usable string.
 *
 * @csspart link - The `<a>` element.
 * @csspart icon - The `vm-icon-next` instance.
 *
 * @example
 * ```html
 * <vm-video-comment-attachment-link
 *   href="/media/comments/notes.pdf"
 * ></vm-video-comment-attachment-link>
 * ```
 */
@customElement('vm-video-comment-attachment-link')
export class VmVideoCommentAttachmentLink extends LitElement {
    static override styles = Styles;

    /** URL of the attachment. */
    @property()
    href = '';

    /** Optional filename used for the accessible name. */
    @property()
    filename = '';

    private _accessibleName(): string {
        if (this.filename) return this.filename;
        const derived = filenameFromHref(this.href);
        return derived || 'Open attachment';
    }

    override render() {
        if (!this.href) return nothing;
        return html`
            <a
                class="vm-video-comment-attachment-link"
                part="link"
                href=${this.href}
                target="_blank"
                rel="noopener"
                aria-label=${this._accessibleName()}
            >
                <vm-icon-next part="icon" name="file-06"></vm-icon-next>
            </a>
        `;
    }
}

/** Extract the last path segment of a URL, decoded. Empty string on failure. */
function filenameFromHref(href: string): string {
    if (!href) return '';
    try {
        const pathname = new URL(href, window.location.origin).pathname;
        const last = pathname.split('/').filter(Boolean).pop() ?? '';
        return decodeURIComponent(last);
    } catch {
        return '';
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'vm-video-comment-attachment-link': VmVideoCommentAttachmentLink;
    }
}
